import { Info } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import ExpertResumesLayout from "../../../components/ExpertResumesLayout/ExpertResumesLayout";
import Loader from "../../../components/Loader";
import Table from "../../../components/Table/Table";
import { getJobsRoutes } from "../../../handlers/navigationItems";
import { ResumeDataService } from "../../../services/create-resume/resume-data";
import ViewJob from "./ViewJob";

const columns = [
	{
		key: "jobTitle",
		label: "Job Title",
		icon: "account",
		editable: false,
		showProfile: true,
		image: "company.logo_url",
		imageClassName: "w-10",
		visible: true,
	},
	{ key: "jobStreetAddress", label: "Job Street Address", icon: "account", editable: false, visible: true },
	{ key: "employmentType", label: "Employment Type", icon: "account", editable: false, visible: true },
	{ key: "jobExpertise", label: "Job Expertise", icon: "account", editable: false, visible: true },
	{ key: "numberOfOpening", label: "Number Of Opening", icon: "account", editable: false, visible: true },
	{ key: "deadline", label: "Deadline Data", icon: "account", editable: false, visible: true },
	{ key: "jobStatus", label: "Job Status", icon: "account", editable: false, visible: true },
	{ key: "actions", label: "Actions", icon: "gear", editable: false, visible: true, isAction: true },
];
const statusOfJobs = { Draft: "Draft", Rejected: "Rejected", Posted: "Posted" };

// from Job title - 2 Positions => Job title
function removePositionCount(str) {
	return str.replace(/\s*-\s*\d+\s*Positions?$/i, "").trim();
}

function Jobs() {
	const jobService = new ResumeDataService();
	const [jobs, setJobs] = useState([]);
	const [loading, setLoading] = useState(true);
	const [errors, setErrors] = useState("");
	const [jobStatus, setJobsStatus] = useState("Draft"); // Draft, Posted
	const [filteredJobs, setFilteredJobs] = useState([]);
	const [searchValue] = useState("");
	const [searchResults, setSearchResults] = useState([]);
	const [showAddJob] = useState(false);
	const [showProfile, setShowProfile] = useState(false);
	const [selectedJob, setSelectedJob] = useState(null);
	const [selectedJobs, setSelectedJobs] = useState(new Set());
	const [checkAll, setCheckAll] = useState(false);
	const [showDuplicates, setShowDuplicates] = useState(false);

	function onUpdate(key, value, index) {
		setSearchResults((prev) => {
			return prev.map((item, i) => {
				if (index === i) {
					return { ...item, [key]: value };
				}
				return item;
			});
		});
	}

	function handleCheckAll(e) {
		const isChecked = e.target.checked;
		setCheckAll(isChecked);

		if (isChecked) {
			const allJobIds = new Set(searchResults.map((job) => job._id));
			setSelectedJobs(allJobIds);
		} else {
			setSelectedJobs(new Set());
		}
	}

	function handleCheck(e, index) {
		const jobId = searchResults[index]._id;
		const newSelectedJobs = new Set(selectedJobs);

		if (e.target.checked) {
			newSelectedJobs.add(jobId);
		} else {
			newSelectedJobs.delete(jobId);
		}

		setSelectedJobs(newSelectedJobs);
		setCheckAll(newSelectedJobs.size === searchResults.length && searchResults.length > 0);
	}

	function deleteSelectedJobs() {
		if (selectedJobs.size === 0) {
			toast.error("Please select jobs to delete");
			return;
		}

		if (window.confirm(`Are you sure you want to delete ${selectedJobs.size} job(s)? This action cannot be undone.`)) {
			setLoading(true);
			const deletePromises = Array.from(selectedJobs).map(
				(jobId) =>
					new Promise((resolve, reject) => {
						jobService.deleteJobById(
							jobId,
							() => resolve(),
							(error) => reject(error),
						);
					}),
			);

			Promise.allSettled(deletePromises)
				.then((results) => {
					const failures = results.filter((result) => result.status === "rejected");
					if (failures.length > 0) {
						toast.error(`Failed to delete ${failures.length} job(s)`);
					} else {
						toast.success(`Successfully deleted ${selectedJobs.size} job(s)`);
					}
					setSelectedJobs(new Set());
					setCheckAll(false);
					getJobs();
				})
				.catch(() => {
					toast.error("An error occurred while deleting jobs");
					setLoading(false);
				});
		}
	}

	const findDuplicates = (arr) => {
		const uniqueSet = new Set();
		const duplicateSet = new Set();

		arr.forEach((job) => {
			const key =
				`${removePositionCount(job.jobTitle).toLowerCase()}_${job?.company ? job?.company?._id : Math.random(36).toString()}_${job.jobLocation.region}`.toLowerCase();
			if (uniqueSet.has(key)) {
				duplicateSet.add(key);
			} else {
				uniqueSet.add(key);
			}
		});

		return arr.map((job) => {
			const key =
				`${removePositionCount(job.jobTitle).toLowerCase()}_${job?.company?._id}_${job.jobLocation.region}`.toLowerCase();
			return {
				...job,
				isDuplicate: duplicateSet.has(key),
			};
		});
	};

	function sortByColumn(key, direction = "asc") {
		const sorted = searchResults.sort((a, b) => {
			const valueA = a[key] || "";
			const valueB = b[key] || "";

			if (direction === "asc") {
				return valueA.localeCompare(valueB);
			} else {
				return valueB.localeCompare(valueA);
			}
		});

		setSearchResults([...sorted]);
	}

	function getJobs() {
		setLoading(true);

		// Fetch both draft/rejected jobs and published jobs
		Promise.all([
			new Promise((resolve, reject) => {
				jobService.getDraftJobs(
					(response) => resolve(response.data || []),
					(error) => reject(error),
				);
			}),
			new Promise((resolve, reject) => {
				jobService.getPublishedJobs(
					(response) => resolve(response.data || []),
					(error) => reject(error),
				);
			}),
		])
			.then(([draftJobs, publishedJobs]) => {
				// Combine both arrays and remove duplicates based on _id
				const allJobs = [...draftJobs, ...publishedJobs];
				const uniqueJobs = allJobs.filter((job, index, self) => index === self.findIndex((j) => j._id === job._id));

				setLoading(false);
				setJobs(findDuplicates(uniqueJobs));
			})
			.catch((error) => {
				setLoading(false);
				setJobs([]);
				setErrors(error.response?.data?.message || "An error occurred while fetching jobs");
			});
	}

	useEffect(() => {
		getJobs();
	}, []);

	useEffect(() => {
		const data = jobs.filter((job) => {
			const matchesStatus = job.jobStatus === jobStatus;
			const shouldInclude = showDuplicates ? true : !job.isDuplicate;
			return matchesStatus && shouldInclude;
		});
		setFilteredJobs(data);
	}, [jobStatus, jobs, showDuplicates]);

	useEffect(() => {
		const results = filteredJobs.filter(
			(job) =>
				job.jobTitle.toLowerCase().includes(searchValue.toLowerCase()) ||
				job.companyName.toLowerCase().includes(searchValue.toLowerCase()),
		);

		// Add duplicate badge to job titles for duplicate jobs
		const resultsWithBadges = results.map((job) => ({
			...job,
			jobTitle: job.isDuplicate ? `[Duplicate] ${job.jobTitle}` : job.jobTitle,
		}));

		setSearchResults(resultsWithBadges);

		// Reset selection when search results change
		setSelectedJobs(new Set());
		setCheckAll(false);
	}, [jobs, filteredJobs, searchValue]);

	function deleteJob(id) {
		setLoading(true);
		jobService.deleteJobById(
			id,
			() => {
				getJobs();
				setShowProfile(false);
			},
			(error) => {
				toast.error(error?.response?.data?.message);
				setErrors(error);
				setLoading(false);
			},
		);
	}

	const duplicateJobs = useMemo(() => {
		//TODO: Filter by job status == jobStatus then allow “show duplicates” if equal to posted
		return jobs.filter((job) => job.isDuplicate && job.jobStatus !== "Posted");
	}, [jobs]);

	function deleteDuplicates() {
		duplicateJobs.forEach((job) => {
			deleteJob(job._id);
		});
	}

	return (
		<ExpertResumesLayout navItems={getJobsRoutes()} headingTitle="Jobs Listing" headingIcon="arrow-left">
			<div className="pl-4 my-resumes-section">
				{loading ? (
					<Loader />
				) : (
					<>
						{errors && (
							<div className="alert alert-danger" role="alert">
								{errors}
							</div>
						)}
						<div className="row px-3">
							<div className="w-full">
								{duplicateJobs.length > 0 && (
									<div className="mb-4 bg-yellow-100 border-yellow-700 rounded-sm p-3 border flex items-center gap-4">
										<Info fill="#fef9c3" />
										<div className="flex items-center justify-between w-full">
											<p>{duplicateJobs.length} duplicate jobs found</p>
											<button className="btn btn-danger" onClick={deleteDuplicates}>
												Delete Duplicates
											</button>
										</div>
									</div>
								)}
								<div className="mb-4 flex items-center gap-3">
									<label className="flex items-center gap-2 cursor-pointer">
										<input
											type="checkbox"
											checked={showDuplicates}
											onChange={(e) => setShowDuplicates(e.target.checked)}
											className="form-checkbox h-4 w-4 text-blue-600"
										/>
										<span className="text-sm font-medium text-gray-700">Show duplicates</span>
									</label>
								</div>
								{selectedJobs.size > 0 && (
									<div className="mb-4 bg-blue-50 border-blue-200 rounded-sm p-3 border flex items-center gap-4">
										<div className="flex items-center justify-between w-full">
											<p>{selectedJobs.size} job(s) selected</p>
											<button className="btn btn-danger" onClick={deleteSelectedJobs}>
												Delete Selected ({selectedJobs.size})
											</button>
										</div>
									</div>
								)}
								<Table
									data={searchResults}
									columns={columns}
									checkAll={checkAll}
									onCheckAll={handleCheckAll}
									onCheck={handleCheck}
									selectedJobs={selectedJobs}
									onUpdate={onUpdate}
									sortByColumn={sortByColumn}
									showAddForm={showAddJob}
									showProfile={showProfile}
									ProfileComponent={ViewJob}
									setShowProfile={setShowProfile}
									setSelectedItem={setSelectedJob}
									selectedItem={selectedJob}
									onDelete={deleteJob}
									tabs={[
										{
											name: `Draft (${jobs.filter((job) => job.jobStatus === statusOfJobs.Draft).length})`,
											key: statusOfJobs.Draft,
										},
										{
											name: `Rejected (${jobs.filter((job) => job.jobStatus === statusOfJobs.Rejected).length})`,
											key: statusOfJobs.Rejected,
										},
										{
											name: `Published (${jobs.filter((job) => job.jobStatus === statusOfJobs.Posted).length})`,
											key: statusOfJobs.Posted,
										},
									]}
									currentTab={jobStatus}
									setCurrentTab={setJobsStatus}
									// onSubmit={onSubmit}
									fetchData={getJobs}
								/>
							</div>
						</div>
					</>
				)}
			</div>
		</ExpertResumesLayout>
	);
}

export default Jobs;
